import { useState } from "react";
import { audio, type AudioSettings as Settings } from "../audio/AudioBus.js";
import { useLocale } from "../i18n.js";
import "./audio-settings.css";

/**
 * Volume controls.
 *
 * Reads from the bus rather than from React state, because the bus is also
 * driven by the event scheduler, which has no component to read from. Every
 * change starts the audio engine too — a player reaching for the volume has
 * unambiguously gestured.
 */
export function AudioSettingsPanel() {
  const { t } = useLocale();
  const [settings, setSettings] = useState<Settings>(() => audio.settings);

  const update = (patch: Partial<Settings>) => {
    audio.unlock();
    setSettings(audio.update(patch));
    if (patch.muted !== true) audio.play("button");
  };

  return (
    <div className="audio-settings">
      <label className="audio-row">
        <span className="audio-label">{t("soundToggle")}</span>
        <button
          type="button"
          className={`toggle${settings.muted ? "" : " toggle-on"}`}
          role="switch"
          aria-checked={!settings.muted}
          onClick={() => update({ muted: !settings.muted })}
        >
          <span className="toggle-knob" />
        </button>
      </label>

      {(["sfx", "ui", "music"] as const).map((channel) => (
        <label key={channel} className="audio-row">
          <span className="audio-label">{LABELS[channel](t)}</span>
          <input
            className="audio-slider"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={settings[channel]}
            disabled={settings.muted}
            onChange={(e) =>
              update({ [channel]: Number(e.target.value) } as Partial<Settings>)
            }
            aria-label={`${LABELS[channel](t)} · ${t("soundToggle")}`}
          />
        </label>
      ))}
    </div>
  );
}

const LABELS = {
  sfx: (t: ReturnType<typeof useLocale>["t"]) => t("audioTable"),
  ui: (t: ReturnType<typeof useLocale>["t"]) => t("audioInterface"),
  music: (t: ReturnType<typeof useLocale>["t"]) => t("audioStings"),
} as const;
